import { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  Input,
  Button,
  IconButton,
  Alert,
  CloseButton,
  Drawer,
  useDisclosure,
  Portal,
  Dialog,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiFilter } from 'react-icons/fi'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import type { Map as MapLibreMap, LngLatBoundsLike } from 'maplibre-gl'
import { contactListApi } from '../lib/api'
import { extractApiMessage } from '../lib/apiError'
import { useIsMobile } from '../hooks/useIsMobile'
import { useErrorToast } from '../hooks/useErrorToast'
import { useUsabilityMark } from '../tools/usability/usabilityMark'
import { ResponsiveModal } from '../components/shared/ResponsiveModal'
import {
  useAnalyticsContacts,
  useAnalyticsDashboard,
  useAnalyticsHighValueMulti,
  useExportToList,
} from '../hooks/useAnalyticsMap'
import { ApplyTagsModal } from '../components/analyticsMap/ApplyTagsModal'
import { CreateTurfModal } from '../components/analyticsMap/CreateTurfModal'
import { TurfReviewMapModal } from '../components/campaign/dailyReview/TurfReviewMapModal'
import { useDailyReview, useCampaignDetail } from '../hooks/useCampaign'
import { useAnalyticsPolygon } from '../hooks/useAnalyticsPolygon'
import { useClientConfig } from '../hooks/useClientConfig'
import { useAvailableStates } from '../hooks/useAvailableStates'
import { AnalyticsDashboard } from '../components/analyticsMap/AnalyticsDashboard'
import { AnalyticsMapView } from '../components/analyticsMap/AnalyticsMapView'
import { MapOverlayIsland } from '../components/analyticsMap/MapOverlayIsland'
import { ContactsPanel } from '../components/analyticsMap/ContactsPanel'
import { GeoCompareMap } from '../components/analyticsMap/GeoCompareMap'
import { CompareLayersPanel } from '../components/analyticsMap/CompareLayersPanel'
import {
  DEFAULT_PANE_SPEC, comparePaneOption, comparePaneRequest,
  autoComparable, resolveScalesLocked, sharedDomain as computeSharedDomain, fitDomain,
} from '../components/analyticsMap/comparePane'
import type { ComparePaneSpec } from '../components/analyticsMap/comparePane'
import { HighValueVoterPanel } from '../components/analyticsMap/HighValueVoterPanel'
import { ActionBar } from '../components/analyticsMap/ActionBar'
import { ScopeConfirmationModal } from '../components/analyticsMap/ScopeConfirmationModal'
import { ContactDetailModal } from '../components/contactDetail/ContactDetailModal'
import { useSidebarContext } from '../components/layout/DashboardLayout'
import { useSidebarAutoCollapse } from './useSidebarAutoCollapse'
import { CollapsibleSection } from '../components/analyticsMap/CollapsibleSection'
import { AreaLayersPanel, DEFAULT_AREA_LAYER_STATE, selectedMetricOption } from '../components/analyticsMap/AreaLayersPanel'
import type { AreaLayerState } from '../components/analyticsMap/AreaLayersPanel'
import { UnitScorecard } from '../components/analyticsMap/UnitScorecard'
import type { UnitAction } from '../components/analyticsMap/UnitScorecard'
import { buildChoroplethGeoJson, computeDomain, fillColorExpression } from '../components/analyticsMap/choropleth'
import { buildQuadrantGeoJson, quadrantFillExpression } from '../components/analyticsMap/bivariate'
import { buildSwingGeoJson } from '../components/analyticsMap/swing'
import { useDistrictBounds, useGeoLiveMetrics, useGeoMetricKeys, useGeoMetrics, useGeoUnits, useGeoUnitsVersion } from '../hooks/useGeoAnalytics'
import { UNION_UNIVERSE_FILTER_GROUPS, UNIVERSE_METRIC_OPTION, US_STATE_BOUNDS, buildMetricKey, defaultUnitTypeFor, initialMapStateFor, supportsLiveMetrics } from '../types/geoAnalytics'
import type { FilterGroup, FilterGroupRequest } from '../types/contactList'
import type {
  BoundingBox,
  ChartPanelConfig,
  ScopeType,
} from '../types/analyticsMap'
import { DEFAULT_VIEW_STATE } from '../lib/mapConfig'

// Compare mode (Task 16) starts both panes centered on the same default view as
// the single map (see DEFAULT_VIEW_STATE) — SyncedView only tracks lng/lat/zoom.
const DEFAULT_COMPARE_VIEW = {
  longitude: DEFAULT_VIEW_STATE.longitude,
  latitude: DEFAULT_VIEW_STATE.latitude,
  zoom: DEFAULT_VIEW_STATE.zoom,
}

// =====================================================================
// HELPERS
// =====================================================================

function todayStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function toFilterGroupRequests(groups: FilterGroup[]): FilterGroupRequest[] {
  return groups.map(g => ({
    groupIndex: g.groupIndex,
    filters: g.filters.map(f => ({
      fieldName: f.fieldName,
      operator: f.operator,
      value: f.value,
      displayOrder: f.displayOrder,
    })),
  }))
}

// =====================================================================
// PAGE COMPONENT
// =====================================================================

export function AnalyticsMapPage() {
  // Auto-collapse dashboard sidebar (restores prior state on exit — DT-3)
  const sidebarContext = useSidebarContext()
  useSidebarAutoCollapse(sidebarContext)

  // Filter state
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>([])
  const [selectedListId, setSelectedListId] = useState<string | null>(null)

  // Fly-to-list: fetch contact locations for the selected list and compute bounds
  const { data: listLocations, error: listLocationsError } = useQuery({
    queryKey: ['lists', selectedListId, 'contacts-with-locations'],
    queryFn: () => contactListApi.getContactsWithLocations(selectedListId!).then(r => r.data),
    enabled: !!selectedListId,
    staleTime: 60_000,
  })
  useErrorToast(listLocationsError, 'Could not show this list on the map', 'Could not load this list’s locations.')

  const listBounds = useMemo<LngLatBoundsLike | null>(() => {
    if (!listLocations || listLocations.length === 0) return null
    const lngs = listLocations.map(c => c.longitude)
    const lats = listLocations.map(c => c.latitude)
    return [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ]
  }, [listLocations])

  // Map viewport
  const [bbox, setBbox] = useState<BoundingBox | null>(null)

  // Polygon selection
  const polygon = useAnalyticsPolygon()

  // Area layers (choropleth) + unit scorecard
  const [areaLayer, setAreaLayer] = useState<AreaLayerState>(DEFAULT_AREA_LAYER_STATE)

  // DT-PROD-7: seed the state selector from the acting committee's default map
  // state (GET /api/config, bootstrapped via useClientConfig — see Task 9) the
  // first time it becomes available. This ONLY sets the initial value: once
  // seeded, the existing fitKey/flyToBounds effect below (keyed on
  // areaLayer.state) picks it up and flies the camera there exactly as it would
  // for a manual dropdown pick — no separate zoom logic needed here.
  //
  // Guarded with a ref so this fires at most once, and only applies the default
  // if the user hasn't already picked a state themselves (functional update
  // checks state at apply time, not at effect-schedule time, so a race between
  // the async config fetch and an early manual pick always favors the user).
  const { data: clientConfig } = useClientConfig()
  // Bug 7 (2026-10-23): with no committee default map state, a single-state locality grant
  // (available-states is already narrowed to the acting committee's grant server-side) seeds
  // the State instead of opening on an empty CONUS view.
  const { data: availableStates } = useAvailableStates()
  const appliedDefaultMapStateRef = useRef(false)
  // DT-PROD-7(i): latched by handleAreaLayerChange the moment the user manually changes the
  // GEOGRAPHY (mirrors userOverrodeDefaultZoomRef, which does the same for the state). A
  // manual pick always wins over the committee default, including during the config-resolve
  // window, and also disarms the empty-boundaries fallback below.
  const userPickedUnitTypeRef = useRef(false)
  useEffect(() => {
    if (appliedDefaultMapStateRef.current) return
    // The grant fallback waits for config to resolve, so a committee default (state + district
    // geography) arriving later is never pre-empted by the one-shot latch below.
    if (clientConfig === undefined) return
    const defaultState = initialMapStateFor(clientConfig?.defaultMapState, availableStates)
    if (!defaultState) return
    appliedDefaultMapStateRef.current = true
    // DT-PROD-7(i): the geography rides along in the SAME one-shot seed (one setAreaLayer,
    // no second seeding effect — keeps the DT-BUG-1 set-state-in-effect surface flat).
    const seededUnitType = defaultUnitTypeFor(clientConfig?.defaultMapDistrictType)
    setAreaLayer(prev => (prev.state ? prev : {
      ...prev,
      state: defaultState,
      unitType: userPickedUnitTypeRef.current ? prev.unitType : seededUnitType,
    }))
  }, [clientConfig, availableStates])

  // Task 10 (district-locality-enforcement, Wave 3 COSMETIC): when the committee also
  // has a default map DISTRICT (state + type + code all set), zoom to that district's
  // bounding box instead of the whole state on open. This is a more specific fly-to
  // target than the state-extent seed above, so it must WIN — see the fit effect below,
  // which applies this once the fetch resolves (naturally after the synchronous state
  // seed). On 404/empty (defaultDistrictParams query resolves to null — see
  // useDistrictBounds) we do nothing further: the state-extent zoom already applied
  // above stands as the fallback. Enforcement of which contacts are visible is unrelated
  // and already handled server-side (Wave 1) — this is display-only.
  //
  // Memoized on the three primitive strings so it's referentially stable across renders
  // that don't actually change the committee's default-district config — otherwise every
  // render would produce a new object, forcing the effects below (which list it as a
  // dependency) to re-evaluate on every render instead of only when it matters.
  const defaultDistrictParams = useMemo(() => (
    clientConfig?.defaultMapState
      && clientConfig?.defaultMapDistrictType
      && clientConfig?.defaultMapDistrictCode
      ? {
          state: clientConfig.defaultMapState,
          type: clientConfig.defaultMapDistrictType,
          code: clientConfig.defaultMapDistrictCode,
        }
      : null
  ), [clientConfig?.defaultMapState, clientConfig?.defaultMapDistrictType, clientConfig?.defaultMapDistrictCode])
  const defaultDistrictBoundsQuery = useDistrictBounds(defaultDistrictParams)

  // Guards the district-zoom effect further down (declared here, ABOVE the fit effect
  // that also reads it, for readability): flips true once that effect has made its
  // one-time decision (district envelope, or falling back to the state/list extent on
  // 404/empty), so it never re-applies.
  const appliedDefaultMapDistrictRef = useRef(false)
  // Fix round 1: latched by handleAreaLayerChange the moment the user manually changes
  // the area-layer STATE (as opposed to the DT-PROD-7 seed effect, which sets state
  // directly and never touches this ref). An explicit user pick must win immediately
  // over a still-pending default-district zoom — before this fix, a manual state change
  // made during the config/bounds resolve window was silently suppressed and then
  // overridden once the default district's bounds arrived. See both effects below.
  const userOverrodeDefaultZoomRef = useRef(false)
  const handleAreaLayerChange = useCallback((next: AreaLayerState) => {
    setAreaLayer(prev => {
      if (next.state !== prev.state) userOverrodeDefaultZoomRef.current = true
      if (next.unitType !== prev.unitType) userPickedUnitTypeRef.current = true
      return next
    })
  }, [])

  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null)
  // WKT of a unit clicked on the choropleth, used to scope export/apply-tags/create-turf
  // the same way a drawn polygon does (see request builders below).
  const [unitActionWkt, setUnitActionWkt] = useState<string | null>(null)

  // Contacts layer: off by default (base+overlay model — people never render unprompted).
  const [showContacts, setShowContacts] = useState(false)

  // High-value voter state (multi-profile)
  const [selectedScoringProfiles, setSelectedScoringProfiles] = useState<Set<string>>(new Set())
  const [profileThresholds, setProfileThresholds] = useState<Record<string, number>>({})
  const [dimNonHighlighted, setDimNonHighlighted] = useState(false)

  // Stackable chart panels
  const [chartPanels, setChartPanels] = useState<ChartPanelConfig[]>([
    { id: '1', chartType: 'ideology' },
  ])

  // Export modal
  const { open: isExportOpen, onOpen: onExportOpen, onClose: onExportClose } = useDisclosure()
  const [exportLabel, setExportLabel] = useState('')
  const exportMutation = useExportToList()

  // Scope confirmation modal
  const { open: isScopeOpen, onOpen: onScopeOpen, onClose: onScopeClose } = useDisclosure()
  const [pendingScopeAction, setPendingScopeAction] = useState<
    'applyTags' | 'exportToList' | 'createTurf' | null
  >(null)
  const [confirmedScope, setConfirmedScope] = useState<ScopeType>('visible')

  // Apply tags & create turf modals
  const { open: isApplyTagsOpen, onOpen: onApplyTagsOpen, onClose: onApplyTagsClose } = useDisclosure()
  const { open: isCreateTurfOpen, onOpen: onCreateTurfOpen, onClose: onCreateTurfClose } = useDisclosure()

  // Recut modal after analytics export merge
  const { open: isRecutOpen, onOpen: onRecutOpen, onClose: onRecutClose } = useDisclosure()
  const [recutCampaignId, setRecutCampaignId] = useState<string | undefined>(undefined)
  const { data: recutReview } = useDailyReview(recutCampaignId, todayStr())
  const { data: recutCampaignDetail } = useCampaignDetail(recutCampaignId)
  const navigate = useNavigate()
  const location = useLocation()

  // Contact detail modal
  const { open: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure()
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null)

  // Mobile: bottom sheet for filters/layers sidebar + dismissible desktop-tools notice
  const { open: isFiltersOpen, onOpen: onFiltersOpen, onClose: onFiltersClose } = useDisclosure()
  const [showDesktopToolsNotice, setShowDesktopToolsNotice] = useState(true)

  // Seed from the List Overview page's "View on map" / "Tag universe" handoff.
  const listHandoffRef = useRef(false)
  useEffect(() => {
    if (listHandoffRef.current) return
    const state = location.state as { listId?: string; action?: 'tag' | 'create' } | null
    if (!state?.listId) return
    listHandoffRef.current = true
    setSelectedListId(state.listId)
    setShowContacts(true) // replicate ContactsPanel's own auto-enable (bypassed at page level)
    if (state.action === 'tag') onApplyTagsOpen()
  }, [location.state, onApplyTagsOpen])

  // Debounce bbox
  const bboxTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleMapMove = useCallback((newBbox: BoundingBox) => {
    if (bboxTimeoutRef.current) clearTimeout(bboxTimeoutRef.current)
    bboxTimeoutRef.current = setTimeout(() => {
      setBbox(newBbox)
    }, 500)
  }, [])

  // Field metadata & tags
  const { data: fieldMetadata = [] } = useQuery({
    queryKey: ['lists', 'field-metadata'],
    queryFn: () => contactListApi.getFieldMetadata().then(r => r.data),
  })

  const { data: tags = [] } = useQuery({
    queryKey: ['lists', 'tags'],
    queryFn: () => contactListApi.getTags().then(r => r.data),
  })

  // Build request objects
  const filterGroupRequests = useMemo(
    () => toFilterGroupRequests(filterGroups),
    [filterGroups]
  )

  // Area layers (choropleth) — data plumbing
  const baseMetricOption = selectedMetricOption(areaLayer)
  const layerReady = (!!baseMetricOption || areaLayer.mode !== 'single') && !!areaLayer.state
  // Universe density + union layers route through live-metrics (LIVE_METRIC_UNIT_TYPES — no ward/municipality).
  const universeSupported = supportsLiveMetrics(areaLayer.unitType)
  // Vintage is resolved server-side — GetUnitsAsync / GetMetricsAsync / GetLiveMetricsAsync each
  // fall back to MAX(VintageYear) for (unitType, state) when the client omits it. We deliberately
  // send NO vintage so a client-side constant can never mismatch the ingested data and silently
  // return zero units (the VEST-2020-precincts-vs-2024-constant bug). Do NOT re-pin a literal here.
  const defaultVintage: number | undefined = undefined

  // Discovery: which metric keys actually have data for this state/unit type (feeds AreaLayersPanel's picker + year lists)
  const metricKeysQuery = useGeoMetricKeys(
    areaLayer.state ? { type: areaLayer.unitType, state: areaLayer.state } : null,
  )
  const availableMetricKeys = metricKeysQuery.data ?? []

  // Data-version token for the current (unitType, state) slice — threaded into useGeoUnits
  // so a fresh ingest produces a new query key + URL, bypassing the long-lived HTTP cache
  // on GET /geo/units (fix #4: prior viewers no longer see empty choropleths for up to an hour).
  const { data: unitsVersion } = useGeoUnitsVersion(
    areaLayer.state ? { type: areaLayer.unitType, state: areaLayer.state } : null,
  )
  // Boundary polygons load as soon as a state is chosen — NOT gated on layerReady/a metric — so a
  // selected geography can render unfilled boundaries before any metric is picked (DT-BUG-3). The
  // metric/live queries below stay gated on layerReady. Cheap + cached (useGeoUnits staleTime 10min).
  const unitsQuery = useGeoUnits(
    areaLayer.state
      ? { type: areaLayer.unitType, state: areaLayer.state, vintage: defaultVintage, v: unitsVersion }
      : null,
  )

  // DT-PROD-7(i): if the committee's default district geography has no ingested boundaries in
  // that state, drop to precincts ONCE, silently — the Geography select then truthfully reads
  // "Precincts" and no toast fires. Fires only when the geography is one WE seeded (step 2 of
  // the resolution order: the initial built-in default is 'precinct', so a non-precinct value
  // here with no user pick can only have come from the committee default), the user has not
  // picked a geography, and the units query has genuinely resolved empty (NOT placeholder data
  // from the previous geography — useGeoUnits uses keepPreviousData). If precincts are also
  // absent the map behaves exactly as today for an un-ingested state. The default-district
  // ZOOM is independent (its own bounds endpoint, 404 -> state extent) and unaffected.
  const appliedEmptyUnitsFallbackRef = useRef(false)
  useEffect(() => {
    if (appliedEmptyUnitsFallbackRef.current) return
    if (!appliedDefaultMapStateRef.current) return
    if (userPickedUnitTypeRef.current) return
    if (areaLayer.unitType === 'precinct') return
    if (!unitsQuery.isSuccess || unitsQuery.isPlaceholderData) return
    if ((unitsQuery.data?.features?.length ?? 0) > 0) return
    appliedEmptyUnitsFallbackRef.current = true
    setAreaLayer(prev => (prev.unitType === 'precinct' ? prev : { ...prev, unitType: 'precinct' }))
  }, [areaLayer.unitType, unitsQuery.isSuccess, unitsQuery.isPlaceholderData, unitsQuery.data])

  // Union layers are live-metrics calls against a preset "tracked union member" universe filter
  const isUnionLayer = !!baseMetricOption && baseMetricOption.group === 'union' && !areaLayer.useUniverse && areaLayer.mode !== 'quadrant'
  const unionRequest = layerReady && isUnionLayer
    ? {
        unitType: areaLayer.unitType,
        state: areaLayer.state,
        vintageYear: defaultVintage,
        filterGroups: UNION_UNIVERSE_FILTER_GROUPS,
        ...(areaLayer.metricOptionKey === 'union.leverage'
          ? { ratioDenominatorMetricKey: 'electorate.registered' }
          : {}),
      }
    : null
  const unionQuery = useGeoLiveMetrics(unionRequest)

  // Quadrant mode draws its own lean×turnout layer — suppress the base single-metric fetch
  // (which would otherwise re-fetch a leftover pick, double-fetching lean in the common case).
  const metricKey = baseMetricOption && !areaLayer.useUniverse && baseMetricOption.group !== 'union' && areaLayer.mode !== 'quadrant'
    ? buildMetricKey(baseMetricOption, areaLayer.year)
    : null
  const metricsQuery = useGeoMetrics(
    layerReady && metricKey
      ? { unitType: areaLayer.unitType, state: areaLayer.state, vintageYear: defaultVintage, metricKeys: [metricKey] }
      : null,
  )
  const liveQuery = useGeoLiveMetrics(
    layerReady && areaLayer.useUniverse && areaLayer.mode !== 'quadrant'
      ? { unitType: areaLayer.unitType, state: areaLayer.state, vintageYear: defaultVintage, filterGroups: filterGroupRequests }
      : null,
  )

  const metricRows = areaLayer.useUniverse ? liveQuery.data : isUnionLayer ? unionQuery.data : metricsQuery.data

  // Universe option gets a data-driven domain; static options keep their configured one;
  // union count layers (footprint) scale the same way once live rows come back.
  const activeMetricOption = useMemo(() => {
    if (!baseMetricOption) return null
    if (areaLayer.useUniverse)
      return { ...UNIVERSE_METRIC_OPTION, domain: computeDomain(UNIVERSE_METRIC_OPTION, metricRows ?? []) }
    if (isUnionLayer && baseMetricOption.format === 'count')
      return { ...baseMetricOption, domain: computeDomain(baseMetricOption, metricRows ?? []) }
    return baseMetricOption
  }, [baseMetricOption, areaLayer.useUniverse, isUnionLayer, metricRows])

  const choroplethData = useMemo(() => {
    if (!unitsQuery.data || !metricRows || !activeMetricOption) return null
    return buildChoroplethGeoJson(unitsQuery.data, metricRows, activeMetricOption)
  }, [unitsQuery.data, metricRows, activeMetricOption])

  const choroplethFillColor = useMemo(
    () => (activeMetricOption ? fillColorExpression(activeMetricOption, activeMetricOption.domain) : null),
    [activeMetricOption],
  )

  // Fly-to target: snap the camera to a NEW state or list selection — but never on a
  // geography (unit-type) or metric swap, which must leave the user where they were
  // (usability finding B2: changing geography used to yank the camera back to the whole
  // state). We gate on a fitKey that only changes with the state or the selected list.
  //
  // Area F fix: the state fit uses a STATIC per-state bbox (US_STATE_BOUNDS), decoupled
  // from the metric-gated useGeoUnits query. Previously the state bounds were borrowed
  // from the units FeatureCollection, which is null until a metric/geography makes
  // layerReady true — so selecting a state with no metric picked did nothing, then
  // mis-fired when a metric was later chosen. Static bounds fly immediately on selection.
  const fitKey = selectedListId
    ? `list:${selectedListId}`
    : areaLayer.state ? `state:${areaLayer.state}` : null
  const [flyToBounds, setFlyToBounds] = useState<LngLatBoundsLike | null>(null)
  const lastFitKeyRef = useRef<string | null>(null)

  // A selected list is a deliberate, specific zoom and takes precedence; otherwise
  // fit the selected state's static extent. Factored out so the Task 10 district
  // effect below can apply the SAME fallback itself when there's no district
  // envelope to use — see that effect's comment for why it can't just let this
  // effect run unmodified on the very first fit.
  const computeStateOrListBounds = useCallback((): LngLatBoundsLike | null => {
    return selectedListId
      ? listBounds
      : areaLayer.state
        ? US_STATE_BOUNDS[areaLayer.state] ?? null
        : null
  }, [selectedListId, listBounds, areaLayer.state])

  useEffect(() => {
    if (!fitKey || fitKey === lastFitKeyRef.current) return
    // Task 10: a default map DISTRICT is strictly more specific than this state/list
    // fit, and must win — UNLESS the user has explicitly picked a state themselves
    // (handleAreaLayerChange latches userOverrodeDefaultZoomRef the instant that
    // happens), in which case the explicit pick always wins instead (fix round 1: an
    // async default must never override an already-made user choice, even one made
    // during the config/bounds resolve window).
    //
    // So: on the very first fit (lastFitKeyRef still null) with a default district
    // configured AND no user override yet, defer entirely to the district effect
    // below — it owns the initial decision and applies either the district envelope
    // or this exact same fallback itself. Without this, a same-tick resolution (e.g.
    // a warm React Query cache) can have BOTH effects fire in one commit; letting
    // this one apply unconditionally first would then get overwritten out of
    // order relative to `areaLayer.state`'s own seed effect, or — worse — clobber
    // a district bounds that already landed. Once the district effect has made
    // that initial call (ref flips true), just sync the key here and stop.
    //
    // Every LATER fitKey change — a manual state/list pick (userOverride true) or
    // any change once the district decision has already landed — proceeds exactly
    // as it did before Task 10: compute and apply immediately.
    if (lastFitKeyRef.current === null && defaultDistrictParams && !userOverrodeDefaultZoomRef.current) {
      if (!appliedDefaultMapDistrictRef.current) return
      lastFitKeyRef.current = fitKey
      return
    }
    const bounds = computeStateOrListBounds()
    if (!bounds) return
    lastFitKeyRef.current = fitKey
    setFlyToBounds(bounds)
  }, [fitKey, defaultDistrictParams, computeStateOrListBounds])

  // Task 10: apply the default-district zoom ONCE it resolves. Ref-guarded so this
  // can only fire a single time per page mount, mirroring appliedDefaultMapStateRef
  // above. Waits for `fitKey` to be non-null (i.e. the state seed above has actually
  // landed) before deciding ANYTHING — reading `areaLayer.state` before that seed
  // takes effect would compute the fallback bounds against the pre-seed ('') state.
  // Also waits for the bounds fetch to settle (`isPending`) rather than acting on a
  // transient `undefined`. React Query owns the fetch lifecycle here (no manual
  // promise), so there is no separate setState-after-unmount hazard to guard: an
  // unmounted component simply never re-renders to run this effect's body again.
  //
  // Fix round 1: bails out immediately if the user has already overridden the
  // default (userOverrodeDefaultZoomRef) — the fit effect above already applied
  // their pick's own extent, and this effect must abandon its pending zoom rather
  // than fly the camera back to the committee default underneath them.
  useEffect(() => {
    if (appliedDefaultMapDistrictRef.current) return
    if (userOverrodeDefaultZoomRef.current) return
    if (!defaultDistrictParams) return
    if (!fitKey) return
    if (defaultDistrictBoundsQuery.isPending) return
    appliedDefaultMapDistrictRef.current = true
    const bounds = defaultDistrictBoundsQuery.data
    if (bounds && bounds.length === 4) {
      setFlyToBounds([bounds[0], bounds[1], bounds[2], bounds[3]])
      return
    }
    // 404/empty -> apply the same fallback the fit effect above deferred to us.
    const fallback = computeStateOrListBounds()
    if (fallback) setFlyToBounds(fallback)
  }, [defaultDistrictParams, fitKey, defaultDistrictBoundsQuery.isPending, defaultDistrictBoundsQuery.data, computeStateOrListBounds])

  // Quadrant mode: two precomputed metrics (lean + turnout) regardless of the metric picker
  const quadrantActive = areaLayer.mode === 'quadrant' && layerReady && areaLayer.quadrantTurnoutYear != null
  const quadrantMetricsQuery = useGeoMetrics(
    quadrantActive
      ? {
          unitType: areaLayer.unitType,
          state: areaLayer.state,
          vintageYear: defaultVintage,
          metricKeys: ['lean', `turnout.general.${areaLayer.quadrantTurnoutYear}`],
        }
      : null,
  )

  const quadrantData = useMemo(() => {
    if (!quadrantActive || !unitsQuery.data || !quadrantMetricsQuery.data) return null
    const leanRows = quadrantMetricsQuery.data.filter(r => r.metricKey === 'lean')
    const turnoutRows = quadrantMetricsQuery.data.filter(r => r.metricKey.startsWith('turnout.general.'))
    return buildQuadrantGeoJson(unitsQuery.data, leanRows, turnoutRows)
  }, [quadrantActive, unitsQuery.data, quadrantMetricsQuery.data])

  // Swing mode: two lean cycles → arrows (single-metric mode only, lean picked, both years set/distinct)
  const swingActive = areaLayer.mode === 'single' && areaLayer.swingEnabled
    && areaLayer.metricOptionKey === 'lean'
    && areaLayer.swingFromYear != null && areaLayer.swingToYear != null
    && areaLayer.swingFromYear !== areaLayer.swingToYear
  const swingMetricsQuery = useGeoMetrics(
    swingActive && layerReady
      ? {
          unitType: areaLayer.unitType,
          state: areaLayer.state,
          vintageYear: defaultVintage,
          metricKeys: [`lean.${areaLayer.swingFromYear}`, `lean.${areaLayer.swingToYear}`],
        }
      : null,
  )

  const swingData = useMemo(() => {
    if (!swingActive || !unitsQuery.data || !swingMetricsQuery.data) return null
    const fromRows = swingMetricsQuery.data.filter(r => r.metricKey === `lean.${areaLayer.swingFromYear}`)
    const toRows = swingMetricsQuery.data.filter(r => r.metricKey === `lean.${areaLayer.swingToYear}`)
    return buildSwingGeoJson(unitsQuery.data, fromRows, toRows)
  }, [swingActive, unitsQuery.data, swingMetricsQuery.data, areaLayer.swingFromYear, areaLayer.swingToYear])

  // Mode-aware routing into the map view: quadrant mode swaps the choropleth source/fill;
  // swing mode dims the base choropleth so arrows read clearly on top.
  // Single/compare with no metric yet falls back to the raw unit polygons so the geography's
  // boundaries render unfilled (mapChoroplethFill stays null → only the boundary line draws, DT-BUG-3).
  const mapChoroplethData = areaLayer.mode === 'quadrant'
    ? quadrantData
    : (choroplethData ?? unitsQuery.data ?? null)
  const mapChoroplethFill = areaLayer.mode === 'quadrant'
    ? (quadrantFillExpression() as unknown[])
    : choroplethFillColor
  const mapOpacity = swingActive ? areaLayer.opacity * 0.4 : areaLayer.opacity

  const contactsRequest = useMemo(() => {
    if (!bbox) return null
    return {
      filterGroups: filterGroupRequests,
      bBox: bbox,
    }
  }, [bbox, filterGroupRequests])

  const dashboardRequest = useMemo(() => {
    return {
      filterGroups: filterGroupRequests,
      polygonWkt: polygon.polygonWkt ?? undefined,
      bBox: bbox ?? undefined,
    }
  }, [filterGroupRequests, polygon.polygonWkt, bbox])

  // Data queries
  const contactsQuery = useAnalyticsContacts(contactsRequest, showContacts && !!bbox)
  const dashboardQuery = useAnalyticsDashboard(dashboardRequest, true)

  // High-value voter query (multi-profile)
  const selectedProfileArray = Array.from(selectedScoringProfiles)
  const highValueMultiRequest = useMemo(() => {
    if (selectedProfileArray.length === 0) return null
    return {
      filterGroups: filterGroupRequests,
      profileNames: selectedProfileArray,
      bBox: bbox ?? undefined,
      topN: 500,
      thresholds: profileThresholds,
    }
  }, [selectedProfileArray.join(','), filterGroupRequests, bbox, profileThresholds])
  const highValueQuery = useAnalyticsHighValueMulti(highValueMultiRequest, selectedProfileArray.length > 0)

  const isAnyLoading = contactsQuery.isFetching || dashboardQuery.isFetching

  // Contact click handler
  const handleContactClick = useCallback((contactId: string) => {
    setSelectedContactId(contactId)
    onDetailOpen()
  }, [onDetailOpen])

  // Map ready handler
  const handleMapReady = useCallback((map: MapLibreMap) => {
    polygon.attachToMap(map)
  }, [polygon.attachToMap])

  // Action bar handlers
  const openScopeModal = useCallback((action: typeof pendingScopeAction) => {
    setPendingScopeAction(action)
    onScopeOpen()
  }, [onScopeOpen])

  const handleScopeConfirm = useCallback((scope: ScopeType) => {
    setConfirmedScope(scope)
    if (pendingScopeAction === 'exportToList') {
      onExportOpen()
    } else if (pendingScopeAction === 'applyTags') {
      onApplyTagsOpen()
    } else if (pendingScopeAction === 'createTurf') {
      onCreateTurfOpen()
    }
    setPendingScopeAction(null)
  }, [pendingScopeAction, onExportOpen, onApplyTagsOpen, onCreateTurfOpen])

  // Unit scorecard action handler — routes into the SAME modals the ActionBar
  // uses, just seeded with the clicked unit's boundary instead of (or in
  // addition to) a hand-drawn polygon. See polygon.polygonWkt ?? unitActionWkt
  // fallbacks below in the export/apply-tags/create-turf request builders.
  const handleUnitAction = useCallback((action: UnitAction, boundaryWkt: string) => {
    setUnitActionWkt(boundaryWkt)
    if (action === 'export') {
      // Export has no scope selector (always filters+polygon) — open it directly.
      onExportOpen()
    } else if (action === 'apply-tags') {
      openScopeModal('applyTags')
    } else if (action === 'create-turf') {
      openScopeModal('createTurf')
    }
  }, [onExportOpen, openScopeModal])

  // Wrapped close handlers: clear the unit-action WKT wherever the existing
  // flow resets/closes these modals, so a stale unit selection can't leak
  // into a later ActionBar-driven (drawn-polygon) action.
  const handleExportModalClose = useCallback(() => {
    setUnitActionWkt(null)
    onExportClose()
  }, [onExportClose])

  const handleApplyTagsModalClose = useCallback(() => {
    setUnitActionWkt(null)
    onApplyTagsClose()
  }, [onApplyTagsClose])

  const handleCreateTurfModalClose = useCallback(() => {
    setUnitActionWkt(null)
    onCreateTurfClose()
  }, [onCreateTurfClose])

  // Export to list handler
  const handleExport = useCallback(() => {
    if (!exportLabel.trim()) return
    exportMutation.mutate(
      {
        filterGroups: filterGroupRequests,
        polygonWkt: polygon.polygonWkt ?? unitActionWkt ?? undefined,
        label: exportLabel.trim(),
      },
      {
        onSuccess: (data) => {
          toaster.create({
            title: 'List created',
            description: (
              <Text>
                List &ldquo;{exportLabel}&rdquo; created.{' '}
                <Text
                  as="span"
                  color="accent"
                  cursor="pointer"
                  textDecoration="underline"
                  onClick={() => navigate(`/dashboard/lists/${data.listId}/edit`)}
                >
                  View list
                </Text>
              </Text>
            ),
            type: 'success',
            duration: 5000,
            closable: true,
          })
          setExportLabel('')
          handleExportModalClose()
        },
        onError: (error) => {
          toaster.create({
            title: 'Export failed',
            description: extractApiMessage(error, 'Could not create list. Please try again.'),
            type: 'error',
            duration: 4000,
            closable: true,
          })
        },
      }
    )
  }, [exportLabel, exportMutation, filterGroupRequests, polygon.polygonWkt, unitActionWkt, navigate, handleExportModalClose])

  // Handle export-to-campaign completion — open recut modal on merge
  const handleTurfExported = useCallback((campaignId: string, merged: boolean) => {
    if (merged) {
      setRecutCampaignId(campaignId)
      onRecutOpen()
    }
  }, [onRecutOpen])

  const handleRecutClose = useCallback(() => {
    onRecutClose()
    setRecutCampaignId(undefined)
  }, [onRecutClose])

  const isMobile = useIsMobile()

  // Usability capture: emit semantic marks on key state transitions. No-op unless
  // a capture session (?usability=1) is recording. See frontend/src/tools/usability/.
  const usabilityMark = useUsabilityMark()
  useEffect(() => {
    usabilityMark('metric', { key: areaLayer.metricOptionKey, year: areaLayer.year })
  }, [areaLayer.metricOptionKey, areaLayer.year, usabilityMark])
  useEffect(() => {
    usabilityMark('mode', { mode: areaLayer.mode })
  }, [areaLayer.mode, usabilityMark])
  useEffect(() => {
    if (selectedUnitId) usabilityMark('unit', { id: selectedUnitId })
  }, [selectedUnitId, usabilityMark])

  // Compare mode (desktop only, Task 16): pane B reuses the same unitType/state/units
  // payload as pane A — only the metric differs. Precomputed metrics only in v1
  // (no union/universe panes on the right side).
  const compareActive = areaLayer.mode === 'compare' && !isMobile && layerReady

  const [paneA, setPaneA] = useState<ComparePaneSpec>(DEFAULT_PANE_SPEC)
  const [paneB, setPaneB] = useState<ComparePaneSpec>(DEFAULT_PANE_SPEC)
  const [scaleLockOverride, setScaleLockOverride] = useState<boolean | null>(null)

  const paneAReq = useMemo(() => comparePaneRequest(paneA), [paneA])
  const paneBReq = useMemo(() => comparePaneRequest(paneB), [paneB])

  const paneAMetricsQuery = useGeoMetrics(
    compareActive && paneAReq?.kind === 'metric'
      ? { unitType: areaLayer.unitType, state: areaLayer.state, vintageYear: defaultVintage, metricKeys: paneAReq.metricKeys }
      : null,
  )
  const paneALiveQuery = useGeoLiveMetrics(
    compareActive && paneAReq?.kind === 'live'
      ? { unitType: areaLayer.unitType, state: areaLayer.state, vintageYear: defaultVintage, filterGroups: paneAReq.filterGroups, ...(paneAReq.ratioDenominatorMetricKey ? { ratioDenominatorMetricKey: paneAReq.ratioDenominatorMetricKey } : {}) }
      : null,
  )
  const paneBMetricsQuery = useGeoMetrics(
    compareActive && paneBReq?.kind === 'metric'
      ? { unitType: areaLayer.unitType, state: areaLayer.state, vintageYear: defaultVintage, metricKeys: paneBReq.metricKeys }
      : null,
  )
  const paneBLiveQuery = useGeoLiveMetrics(
    compareActive && paneBReq?.kind === 'live'
      ? { unitType: areaLayer.unitType, state: areaLayer.state, vintageYear: defaultVintage, filterGroups: paneBReq.filterGroups, ...(paneBReq.ratioDenominatorMetricKey ? { ratioDenominatorMetricKey: paneBReq.ratioDenominatorMetricKey } : {}) }
      : null,
  )

  const paneARows = paneAReq?.kind === 'metric' ? paneAMetricsQuery.data : paneAReq?.kind === 'live' ? paneALiveQuery.data : undefined
  const paneBRows = paneBReq?.kind === 'metric' ? paneBMetricsQuery.data : paneBReq?.kind === 'live' ? paneBLiveQuery.data : undefined

  // Locked-scale option: data-scaled domain for count formats (universe / union footprint),
  // configured catalogue domain otherwise. This is what the shared (locked) scale is built from.
  const paneALockedOption = useMemo(() => {
    const opt = comparePaneOption(paneA)
    if (!opt) return null
    return opt.format === 'count' ? { ...opt, domain: computeDomain(opt, paneARows ?? []) } : opt
  }, [paneA, paneARows])
  const paneBLockedOption = useMemo(() => {
    const opt = comparePaneOption(paneB)
    if (!opt) return null
    return opt.format === 'count' ? { ...opt, domain: computeDomain(opt, paneBRows ?? []) } : opt
  }, [paneB, paneBRows])

  const scaleAutoComparable = autoComparable(paneALockedOption, paneBLockedOption)
  const scalesLocked = resolveScalesLocked(scaleLockOverride, scaleAutoComparable)

  // Unlocked: each pane's ramp (and legend) fits its own data range — a fixed [0,100] turnout
  // ramp rendered Virginia as one flat colour. Locked keeps the configured/shared domain.
  const paneAActiveOption = useMemo(
    () => (paneALockedOption && !scalesLocked
      ? { ...paneALockedOption, domain: fitDomain(paneALockedOption, paneARows ?? []) }
      : paneALockedOption),
    [paneALockedOption, scalesLocked, paneARows],
  )
  const paneBActiveOption = useMemo(
    () => (paneBLockedOption && !scalesLocked
      ? { ...paneBLockedOption, domain: fitDomain(paneBLockedOption, paneBRows ?? []) }
      : paneBLockedOption),
    [paneBLockedOption, scalesLocked, paneBRows],
  )
  const sharedDom = useMemo(
    () => (scalesLocked && paneAActiveOption && paneBActiveOption
      ? computeSharedDomain(paneAActiveOption, paneAActiveOption.domain, paneBActiveOption, paneBActiveOption.domain)
      : null),
    [scalesLocked, paneAActiveOption, paneBActiveOption],
  )

  const paneAData = useMemo(
    () => (compareActive && unitsQuery.data && paneARows && paneAActiveOption
      ? buildChoroplethGeoJson(unitsQuery.data, paneARows, paneAActiveOption) : null),
    [compareActive, unitsQuery.data, paneARows, paneAActiveOption],
  )
  const paneBData = useMemo(
    () => (compareActive && unitsQuery.data && paneBRows && paneBActiveOption
      ? buildChoroplethGeoJson(unitsQuery.data, paneBRows, paneBActiveOption) : null),
    [compareActive, unitsQuery.data, paneBRows, paneBActiveOption],
  )
  const paneAFill = useMemo(
    () => (paneAActiveOption ? fillColorExpression(paneAActiveOption, sharedDom ?? paneAActiveOption.domain) : null),
    [paneAActiveOption, sharedDom],
  )
  const paneBFill = useMemo(
    () => (paneBActiveOption ? fillColorExpression(paneBActiveOption, sharedDom ?? paneBActiveOption.domain) : null),
    [paneBActiveOption, sharedDom],
  )

  // Mobile A/B metric swap (Task 16): compare is desktop-only, so mobile gets a
  // quick swap between the active metric and a remembered secondary instead.
  const [abSecondary, setAbSecondary] = useState<{ key: string; year: number | null } | null>(null)
  const [abActive, setAbActive] = useState<'A' | 'B'>('A')

  const handleAbSwap = () => {
    if (!abSecondary) {
      // remember current as B, stay on A until user picks something else
      if (areaLayer.metricOptionKey)
        setAbSecondary({ key: areaLayer.metricOptionKey, year: areaLayer.year })
      return
    }
    const current = { key: areaLayer.metricOptionKey!, year: areaLayer.year }
    setAreaLayer({ ...areaLayer, metricOptionKey: abSecondary.key, year: abSecondary.year })
    setAbSecondary(current)
    setAbActive(abActive === 'A' ? 'B' : 'A')
  }

  if (isMobile) {
    // Fits within MobileDashboardShell's scrollable content region (it is NOT
    // the full viewport — it renders below MobileTopBar + CommitteeSwitcher and
    // above the fixed ContextualTabBar). Chrome heights subtracted below:
    //   MobileTopBar    ~60px (py=3 padding [24px] + 32px content [UserAvatar
    //                    "sm" is the tallest child at 32px] + 1px border, rounded up)
    //   CommitteeSwitcher ~40px (Select size="sm" is 32px tall, rounded up for
    //                    safety since it's rendered with no extra padding)
    //   Tab bar clearance 72px + env(safe-area-inset-bottom) (matches the exact
    //                    value MobileDashboardShell itself reserves via its `pb`)
    // Total: 172px + safe-area-inset-bottom. Rounded up (not exact) on purpose —
    // a slightly shorter map is preferable to overflowing behind the tab bar.
    return (
      <Flex
        direction="column"
        h="calc(100dvh - 172px - env(safe-area-inset-bottom, 0px))"
        w="100%"
        position="relative"
        overflow="hidden"
      >
        {/* Full-bleed map */}
        <Box flex={1} position="relative" minH={0}>
          <AnalyticsMapView
            contacts={contactsQuery.data}
            showContacts={showContacts}
            isLoading={isAnyLoading}
            onMapMove={handleMapMove}
            onContactClick={handleContactClick}
            onMapReady={handleMapReady}
            highValueVoters={highValueQuery.data}
            dimNonHighlighted={dimNonHighlighted}
            flyToBounds={flyToBounds}
            choroplethData={mapChoroplethData}
            choroplethFillColor={mapChoroplethFill}
            choroplethOpacity={mapOpacity}
            swingData={swingActive ? swingData : null}
            onUnitClick={setSelectedUnitId}
            geoUnitType={areaLayer.unitType}
          />

          {/* Floating trigger for filters/layers bottom sheet.
              Top-LEFT on purpose: AnalyticsMapView renders its own loading
              indicator at top-right (top={3} right={3} zIndex={10}), which
              fires on every pan/filter fetch — top-left avoids that collision
              and no other map overlay control uses that corner. */}
          <IconButton
            aria-label="Filters & layers"
            onClick={onFiltersOpen}
            position="absolute"
            top={3}
            left={3}
            zIndex={10}
            colorPalette="brand"
            borderRadius="full"
            boxShadow="md"
            minH="44px"
            minW="44px"><FiFilter /></IconButton>

          {/* Mobile A/B metric swap (compare mode is desktop-only, Task 16):
              bottom-right, cleared above the desktop-tools notice below. */}
          {isMobile && areaLayer.mode === 'single' && areaLayer.metricOptionKey && (
            <Button
              position="fixed"
              bottom="calc(140px + env(safe-area-inset-bottom, 0px))"
              right={4}
              size="sm"
              minH="44px"
              borderRadius="full"
              boxShadow="lg"
              colorPalette="brand"
              variant="solid"
              onClick={handleAbSwap}
              zIndex={20}
            >
              {abSecondary ? `A/B: ${abActive}` : 'Pin as B'}
            </Button>
          )}

          {/* Desktop-only tools notice (drawing/bulk actions deferred to desktop) */}
          {showDesktopToolsNotice && (
            <MapOverlayIsland position="absolute" bottom={3} left={3} right={3} zIndex={10}>
              <Alert.Root
                status="info"
                width="100%"
                borderRadius="md"
                boxShadow="md"
                alignItems="flex-start"
              >
                <Alert.Indicator />
                <Alert.Description fontSize="sm" flex={1}>
                  Polygon drawing & bulk actions are available on desktop.
                </Alert.Description>
                <CloseButton size="sm" onClick={() => setShowDesktopToolsNotice(false)} />
              </Alert.Root>
            </MapOverlayIsland>
          )}
        </Box>

        {/* Unit scorecard bottom sheet — data only; bulk actions stay desktop-only */}
        <Drawer.Root placement='bottom' open={!!selectedUnitId} onOpenChange={e => {
          if (!e.open) {
            setSelectedUnitId(null);
          }
        }}>
          <Portal>

            <Drawer.Backdrop />
            <Drawer.Positioner>
              <Drawer.Content borderTopRadius="lg" maxH="60vh">
                <Drawer.Body p={3} pb="env(safe-area-inset-bottom, 16px)">
                  {selectedUnitId && (
                    <UnitScorecard unitId={selectedUnitId} onClose={() => setSelectedUnitId(null)} />
                  )}
                </Drawer.Body>
              </Drawer.Content>
            </Drawer.Positioner>

          </Portal>
        </Drawer.Root>

        {/* Bottom sheet: filters, high-value, dashboard */}
        <Drawer.Root placement='bottom' open={isFiltersOpen} onOpenChange={e => {
          if (!e.open) {
            onFiltersClose();
          }
        }}>
          <Portal>

            <Drawer.Backdrop />
            <Drawer.Positioner>
              <Drawer.Content borderTopRadius="lg" maxH="85vh">
                <Drawer.CloseTrigger asChild><CloseButton size="sm" /></Drawer.CloseTrigger>
                <Drawer.Header>Filters & Layers</Drawer.Header>
                <Drawer.Body pb="env(safe-area-inset-bottom, 16px)" overflowY="auto">
                  <VStack gap={1} align="stretch">
                    {/* Section: Area Layers (choropleth) */}
                    <CollapsibleSection title="Area Layers" defaultOpen>
                      <AreaLayersPanel
                        value={areaLayer}
                        onChange={handleAreaLayerChange}
                        availableMetricKeys={availableMetricKeys}
                        allowCompare={!isMobile}
                      />
                    </CollapsibleSection>

                    {/* Contacts tier: show-contacts toggle + filters + universe density */}
                    <CollapsibleSection title="Contacts" defaultOpen>
                      <ContactsPanel
                        showContacts={showContacts}
                        onShowContactsChange={setShowContacts}
                        useUniverse={areaLayer.useUniverse}
                        onUniverseChange={v => setAreaLayer({ ...areaLayer, useUniverse: v })}
                        universeSupported={universeSupported}
                        hasActiveFilters={filterGroupRequests.length > 0}
                        filterGroups={filterGroups}
                        onFilterGroupsChange={setFilterGroups}
                        fieldMetadata={fieldMetadata}
                        tags={tags}
                        selectedListId={selectedListId}
                        onListIdChange={setSelectedListId}
                      />
                    </CollapsibleSection>

                    {/* Section B: High-Value Voters */}
                    <CollapsibleSection title="High-Value Voters">
                      <HighValueVoterPanel
                        filterGroups={filterGroupRequests}
                        bbox={bbox}
                        selectedProfiles={selectedScoringProfiles}
                        onProfilesChange={setSelectedScoringProfiles}
                        profileThresholds={profileThresholds}
                        onThresholdsChange={setProfileThresholds}
                        dimNonHighlighted={dimNonHighlighted}
                        onDimToggle={setDimNonHighlighted}
                        resultCount={highValueQuery.data?.length}
                        isLoadingResults={highValueQuery.isFetching}
                        resultsError={highValueQuery.error}
                      />
                    </CollapsibleSection>

                    {/* Section D: Dashboard Charts */}
                    <CollapsibleSection title="Dashboard" defaultOpen>
                      <AnalyticsDashboard
                        data={dashboardQuery.data}
                        isLoading={dashboardQuery.isFetching}
                        chartPanels={chartPanels}
                        onChartPanelsChange={setChartPanels}
                      />
                    </CollapsibleSection>
                  </VStack>
                </Drawer.Body>
              </Drawer.Content>
            </Drawer.Positioner>

          </Portal>
        </Drawer.Root>

        {/* Contact detail modal */}
        {selectedContactId && (
          <ContactDetailModal
            contactId={selectedContactId}
            isOpen={isDetailOpen}
            onClose={onDetailClose}
          />
        )}

        {/*
          Selection-action modals (Scope / ApplyTags / CreateTurf / TurfReview) are
          NOT mounted on mobile: they are only reachable via the desktop ActionBar,
          which mobile does not render. See the desktop branch below for these.
        */}

        {/* Export to list modal */}
        <ResponsiveModal isOpen={isExportOpen} onClose={handleExportModalClose} size="sm">
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header fontSize="md">Export Selection as List</Dialog.Header>
              <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
              <Dialog.Body>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  Create a new contact list from the current selection and filters.
                </Text>
                <Input
                  size="sm"
                  placeholder="List name..."
                  value={exportLabel}
                  onChange={e => setExportLabel(e.target.value)}
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') handleExport() }}
                />
              </Dialog.Body>
              <Dialog.Footer>
                <Button size="sm" variant="ghost" mr={2} onClick={handleExportModalClose}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  colorPalette="brand"
                  onClick={handleExport}
                  loading={exportMutation.isPending}
                  disabled={!exportLabel.trim()}
                >
                  Create List
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </ResponsiveModal>
      </Flex>
    );
  }

  return (
    <Flex h="100vh" w="100%" overflow="hidden">
      {/* Analytics Sidebar */}
      <Box
        w="380px"
        minW="380px"
        bg="surface"
        borderRight="1px"
        borderColor="gray.200"
        overflowY="auto"
        h="100%"
        flexShrink={0}
      >
        <Box p={4}>
          {/* Header */}
          <Box mb={4}>
            <Heading size="md">Analytics Map</Heading>
            <Text fontSize="xs" color="gray.500">Visualize and analyze voter data</Text>
          </Box>

          <VStack gap={1} align="stretch">
            {/* Section: Area Layers (choropleth) */}
            <CollapsibleSection title="Area Layers" defaultOpen>
              {areaLayer.mode === 'compare' ? (
                <CompareLayersPanel
                  shared={areaLayer}
                  onSharedChange={handleAreaLayerChange}
                  allowCompare={!isMobile}
                  paneA={paneA}
                  paneB={paneB}
                  onPaneAChange={setPaneA}
                  onPaneBChange={setPaneB}
                  availableMetricKeys={availableMetricKeys}
                  fields={fieldMetadata}
                  tags={tags}
                  scalesLocked={scalesLocked}
                  onToggleLock={checked => setScaleLockOverride(checked)}
                  paneAOption={paneAActiveOption}
                  paneBOption={paneBActiveOption}
                  sharedDomain={sharedDom}
                  scaleAutoComparable={scaleAutoComparable}
                />
              ) : (
                <AreaLayersPanel
                  value={areaLayer}
                  onChange={handleAreaLayerChange}
                  availableMetricKeys={availableMetricKeys}
                  allowCompare={!isMobile}
                />
              )}
            </CollapsibleSection>

            {/* Contacts tier: show-contacts toggle + filters + universe density */}
            <CollapsibleSection title="Contacts" defaultOpen>
              <ContactsPanel
                showContacts={showContacts}
                onShowContactsChange={setShowContacts}
                useUniverse={areaLayer.useUniverse}
                onUniverseChange={v => setAreaLayer({ ...areaLayer, useUniverse: v })}
                universeSupported={universeSupported}
                hasActiveFilters={filterGroupRequests.length > 0}
                filterGroups={filterGroups}
                onFilterGroupsChange={setFilterGroups}
                fieldMetadata={fieldMetadata}
                tags={tags}
                selectedListId={selectedListId}
                onListIdChange={setSelectedListId}
              />
            </CollapsibleSection>

            {/* Section B: High-Value Voters */}
            <CollapsibleSection title="High-Value Voters">
              <HighValueVoterPanel
                filterGroups={filterGroupRequests}
                bbox={bbox}
                selectedProfiles={selectedScoringProfiles}
                onProfilesChange={setSelectedScoringProfiles}
                profileThresholds={profileThresholds}
                onThresholdsChange={setProfileThresholds}
                dimNonHighlighted={dimNonHighlighted}
                onDimToggle={setDimNonHighlighted}
                resultCount={highValueQuery.data?.length}
                isLoadingResults={highValueQuery.isFetching}
                resultsError={highValueQuery.error}
              />
            </CollapsibleSection>

            {/* Section D: Dashboard Charts */}
            <CollapsibleSection title="Dashboard" defaultOpen>
              <AnalyticsDashboard
                data={dashboardQuery.data}
                isLoading={dashboardQuery.isFetching}
                chartPanels={chartPanels}
                onChartPanelsChange={setChartPanels}
              />
            </CollapsibleSection>
          </VStack>
        </Box>
      </Box>

      {/* Right side: Action Bar + Map */}
      <Flex flex={1} direction="column" minW={0}>
        <ActionBar
          isDrawing={polygon.isDrawing}
          polygonWkt={polygon.polygonWkt}
          onDrawSelection={polygon.startDrawing}
          onClearPolygon={polygon.clearPolygon}
          onApplyTags={() => { setUnitActionWkt(null); openScopeModal('applyTags') }}
          onExportToList={() => { setUnitActionWkt(null); openScopeModal('exportToList') }}
          onCreateTurf={() => { setUnitActionWkt(null); openScopeModal('createTurf') }}
        />

        {compareActive ? (
          <Box flex={1} position="relative">
            <GeoCompareMap
              paneA={{
                label: paneAActiveOption ? `${paneAActiveOption.label}${paneA.source === 'metric' && paneA.year ? ` (${paneA.year})` : ''}` : 'Pick a metric',
                data: paneAData,
                fillColor: paneAFill,
              }}
              paneB={{
                label: paneBActiveOption ? `${paneBActiveOption.label}${paneB.source === 'metric' && paneB.year ? ` (${paneB.year})` : ''}` : 'Pick a metric',
                data: paneBData,
                fillColor: paneBFill,
              }}
              opacity={areaLayer.opacity}
              initialView={DEFAULT_COMPARE_VIEW}
              flyToBounds={flyToBounds}
              onUnitClick={setSelectedUnitId}
            />
            {selectedUnitId && (
              <MapOverlayIsland position="absolute" top={3} right={3} w="300px" zIndex={10}>
                <UnitScorecard unitId={selectedUnitId} onClose={() => setSelectedUnitId(null)} />
              </MapOverlayIsland>
            )}
          </Box>
        ) : (
          <Box flex={1} position="relative">
            <AnalyticsMapView
              contacts={contactsQuery.data}
              showContacts={showContacts}
              isLoading={isAnyLoading}
              onMapMove={handleMapMove}
              onContactClick={handleContactClick}
              onMapReady={handleMapReady}
              highValueVoters={highValueQuery.data}
              dimNonHighlighted={dimNonHighlighted}
              flyToBounds={flyToBounds}
              choroplethData={mapChoroplethData}
              choroplethFillColor={mapChoroplethFill}
              choroplethOpacity={mapOpacity}
              swingData={swingActive ? swingData : null}
              onUnitClick={setSelectedUnitId}
              geoUnitType={areaLayer.unitType}
            />

            {selectedUnitId && (
              <MapOverlayIsland position="absolute" top={3} right={3} w="300px" zIndex={10}>
                <UnitScorecard
                  unitId={selectedUnitId}
                  onClose={() => setSelectedUnitId(null)}
                  onAction={handleUnitAction}
                />
              </MapOverlayIsland>
            )}
          </Box>
        )}
      </Flex>

      {/* Contact detail modal */}
      {selectedContactId && (
        <ContactDetailModal
          contactId={selectedContactId}
          isOpen={isDetailOpen}
          onClose={onDetailClose}
        />
      )}

      {/* Scope confirmation modal */}
      <ScopeConfirmationModal
        isOpen={isScopeOpen}
        onClose={onScopeClose}
        onConfirm={handleScopeConfirm}
        hasHighValueProfiles={selectedScoringProfiles.size > 0}
        hasPolygon={!!(polygon.polygonWkt ?? unitActionWkt)}
        // A unit-scorecard action already has one unambiguous boundary — skip
        // the visible/high-value scope choices and go straight to "polygon".
        polygonOnly={!!unitActionWkt}
        title={
          pendingScopeAction === 'applyTags'
            ? 'Apply Tags — Select Scope'
            : pendingScopeAction === 'exportToList'
              ? 'Export to List — Select Scope'
              : 'Create Turf — Select Scope'
        }
        filterGroups={filterGroupRequests}
        profileNames={selectedProfileArray}
        polygonWkt={polygon.polygonWkt ?? unitActionWkt ?? undefined}
        bBox={bbox ?? undefined}
        thresholds={profileThresholds}
      />

      {/* Apply tags modal */}
      <ApplyTagsModal
        isOpen={isApplyTagsOpen}
        onClose={handleApplyTagsModalClose}
        tags={tags}
        scope={confirmedScope}
        filterGroups={filterGroupRequests}
        profileNames={selectedProfileArray}
        polygonWkt={polygon.polygonWkt ?? unitActionWkt ?? undefined}
        bBox={bbox ?? undefined}
        thresholds={profileThresholds}
      />

      {/* Create turf modal */}
      <CreateTurfModal
        isOpen={isCreateTurfOpen}
        onClose={handleCreateTurfModalClose}
        scope={confirmedScope}
        filterGroups={filterGroupRequests}
        profileNames={selectedProfileArray}
        polygonWkt={polygon.polygonWkt ?? unitActionWkt ?? undefined}
        bBox={bbox ?? undefined}
        thresholds={profileThresholds}
        onExported={handleTurfExported}
      />

      {/* Recut modal after analytics export merge */}
      {recutReview && (
        <TurfReviewMapModal
          isOpen={isRecutOpen}
          onClose={handleRecutClose}
          campaignId={recutCampaignId!}
          turfMaps={recutReview.turfMaps}
          turfCoverage={recutReview.turfCoverage}
          reviewDate={todayStr()}
          assignmentMode={recutCampaignDetail?.assignmentMode}
        />
      )}

      {/* Export to list modal */}
      <ResponsiveModal isOpen={isExportOpen} onClose={handleExportModalClose} size="sm">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header fontSize="md">Export Selection as List</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body>
              <Text fontSize="sm" color="gray.600" mb={3}>
                Create a new contact list from the current selection and filters.
              </Text>
              <Input
                size="sm"
                placeholder="List name..."
                value={exportLabel}
                onChange={e => setExportLabel(e.target.value)}
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') handleExport() }}
              />
            </Dialog.Body>
            <Dialog.Footer>
              <Button size="sm" variant="ghost" mr={2} onClick={handleExportModalClose}>
                Cancel
              </Button>
              <Button
                size="sm"
                colorPalette="brand"
                onClick={handleExport}
                loading={exportMutation.isPending}
                disabled={!exportLabel.trim()}
              >
                Create List
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>
    </Flex>
  );
}
